export interface FlowSheetUpload {
	uploadFileId: number;
	fileName: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

export const parseFlowSheetUploadResponse = (
	response: unknown,
): FlowSheetUpload => {
	if (!isRecord(response) || response.success !== true) {
		throw new Error("The upload service did not report a successful upload");
	}

	if (!Array.isArray(response.result) || response.result.length !== 1) {
		throw new Error(
			"The upload service did not return exactly one file record",
		);
	}

	const fileRecord: unknown = response.result[0];
	if (!isRecord(fileRecord)) {
		throw new Error("The upload service returned an invalid file record");
	}

	const uploadFileId = fileRecord.UploadFileId;
	if (
		typeof uploadFileId !== "number" ||
		!Number.isInteger(uploadFileId) ||
		uploadFileId <= 0
	) {
		throw new Error("The upload service returned an invalid UploadFileId");
	}

	const fileName = fileRecord.FileName;
	if (typeof fileName !== "string" || fileName.trim() === "") {
		throw new Error("The upload service returned an invalid FileName");
	}

	return { uploadFileId, fileName };
};

export interface FlowSheet {
	SectionId: number;
	StandId: number;
	StandName: string;
	SortOrder: number | null;
	StandTypeCode: string | null;
	FlowSheetId: number | null;
	IsDiagonal: boolean;
	BilletSize: number | null;
	DisplayName: string | null;
	UploadFileId: number | null;
	FileName: string | null;
	Path: string | null;
	Hash: string | null;
	CreationTime: string | null;
}

export interface SectionFlowSheets {
	drawings: FlowSheet[];
	billetSizes: number[];
}

export const formatBilletSize = (billetSize: number) =>
	`${billetSize}x${billetSize}`;

export const parseBilletSize = (value: string): number | null => {
	const size = Number(value.trim().toLowerCase().split("x")[0]);
	return Number.isInteger(size) && size > 0 ? size : null;
};

export const mergeBilletSizes = (...groups: (number | null)[][]): number[] =>
	[
		...new Set(
			groups.flat().filter((size): size is number => size !== null && size > 0),
		),
	].sort((a, b) => a - b);

export interface FlowSheetRow {
	StandId: number;
	StandName: string;
	FileName: string | null;
	Url: string | null;
}

const buildUploadUrl = (path: string | null) =>
	path ? `/uploads/${encodeURI(path.replace(/\\/g, "/"))}` : null;

export const buildStandDrawingRows = (drawings: FlowSheet[]): FlowSheetRow[] =>
	drawings.map((drawing) => ({
		StandId: drawing.StandId,
		StandName: drawing.StandName,
		FileName: drawing.FileName,
		Url: buildUploadUrl(drawing.Path),
	}));
